import React from 'react';
import RelativeTime from 'components/common/RelativeTime';
import PaymentModal from 'components/modals/paymentModal/PaymentModal';
import axios from 'helpers/axios';
import config from 'helpers/config';
import { getPaymentTypeName } from 'helpers/payments/paymentsService';
import Payment from 'models/PaymentModel';
import RepairOrder from 'models/RepairOrderModel';
import Service from 'models/ServiceModel';
import ServiceItemType from 'models/types/ServiceItemType';
import ServiceAuthorizationStatus from 'models/types/ServiceAuthorizationStatus';
import Price from 'components/common/Price';
import { PlusCircle } from 'lucide-react';

interface Props {
    repairOrder: RepairOrder;
    services: Service[];
}

interface State {
    payments: Payment[];
    isPaymentsModalOpen: boolean;
    payment?: Payment;

    grandTotal: number;
    paidToDate: number;
}

class Payments extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isPaymentsModalOpen: false,
            payments: [],
            grandTotal: 0,
            paidToDate: 0,
        };
    }

    componentDidMount() {
        this.getPayments();
    }

    getPayments() {
        axios
            .get(`${config.apiUrl}/repairOrder/payments/${this.props.repairOrder.id}`)
            .then((response) => {
                this.setState({ payments: response.data });
            })
            .catch((err) => console.log(err));
    }

    getSubtotal() {
        return (this.props.services ?? []).reduce((acc, service) => acc + (service.totalCents || 0), 0);
    }

    getTax() {
        return (this.props.services ?? []).reduce((acc, service) => acc + (service.totalTaxCents || 0), 0);
    }

    getGrandTotal() {
        return (this.props.services ?? []).reduce((acc, service) => acc + (service.grandTotalCents || 0), 0);
    }

    getPaidToDate() {
        return (this.state.payments ?? []).reduce((acc, payment) => acc + (payment.amountCents || 0), 0);
    }

    openPaymentModal(payment?: Payment) {
        if (payment) {
            this.setState({ isPaymentsModalOpen: true, payment });
        } else {
            this.setState({ isPaymentsModalOpen: true });
        }
    }

    getRemainingBalance() {
        // Balance owed is the tax-inclusive grand total less what has been paid.
        return this.getGrandTotal() - this.getPaidToDate();
    }

    getTotalByServiceItemType(serviceItemType: ServiceItemType) {
        const { services } = this.props;
        const servicesToInclude = services?.filter(
            (service) =>
                !(service.isRecommended && service.authorizationStatus !== ServiceAuthorizationStatus.Authorized),
        );

        const totalCents = servicesToInclude
            ?.flatMap((service) => service.items)
            .filter((item) => item.type === serviceItemType)
            .reduce((acc, item) => acc + (item.totalCents || 0), 0);
        return totalCents || 0;
    }

    render() {
        const { repairOrder, services } = this.props;
        const { isPaymentsModalOpen, payment, payments } = this.state;

        return (
            <div>
                {isPaymentsModalOpen && (
                    <PaymentModal
                        repairOrder={{ ...repairOrder, services: services }}
                        onClose={() => {
                            this.setState({ isPaymentsModalOpen: false, payment: undefined });
                            this.getPayments();
                        }}
                        payment={payment}
                        leftToPay={this.getRemainingBalance() / 100}
                    />
                )}

                <div className="mb-4">
                    <button
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-semibold transition-colors flex items-center"
                        onClick={() => this.openPaymentModal()}
                    >
                        <PlusCircle className="w-4 h-4 mr-2" />
                        New Payment
                    </button>
                </div>

                {payments.map((payment) => (
                    <div
                        key={payment.id}
                        className="flex clickable hover:underline mb-2 border-b border-dashed"
                        onClick={() => this.openPaymentModal(payment)}
                    >
                        <div>
                            <div className="font-semibold text-green-600">
                                <Price price={payment.amountCents} />
                            </div>
                            <div className="text-sm text-gray-400 font-semibold">
                                {getPaymentTypeName(payment.paymentType)}
                            </div>
                        </div>
                        <div className="text-gray-600 clickable hover:underline ml-auto">
                            <RelativeTime dateTime={payment.paymentDate} />
                        </div>
                    </div>
                ))}

                <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-1">
                        <span>Total Parts:</span>
                        <span className="font-semibold text-gray-700">
                            <Price price={this.getTotalByServiceItemType(ServiceItemType.Part)} />
                        </span>
                    </div>

                    <div className="flex justify-between py-1">
                        <span>Total Labor:</span>
                        <span className="font-semibold text-gray-700">
                            <Price price={this.getTotalByServiceItemType(ServiceItemType.Labor)} />
                        </span>
                    </div>

                    <div className="flex justify-between py-1">
                        <span>Total Fees:</span>
                        <span className="font-semibold text-gray-700">
                            <Price price={this.getTotalByServiceItemType(ServiceItemType.Fee)} />
                        </span>
                    </div>

                    <div className="flex justify-between py-1 border-t border-gray-200 pt-2">
                        <span className="font-semibold">Subtotal:</span>
                        <span className="font-semibold text-gray-700">
                            <Price price={this.getSubtotal()} />
                        </span>
                    </div>

                    <div className="flex justify-between py-1">
                        <span>Tax:</span>
                        <span className="font-semibold text-gray-700">
                            <Price price={this.getTax()} />
                        </span>
                    </div>

                    <div className="flex justify-between py-2 border-t border-gray-300 font-semibold text-base">
                        <span>Grand Total:</span>
                        <span className="font-semibold text-gray-700">
                            <Price price={this.getGrandTotal()} />
                        </span>
                    </div>

                    <div className="flex justify-between py-1">
                        <span>Paid to date:</span>
                        <span className="font-semibold text-green-700">
                            <Price price={this.getPaidToDate()} />
                        </span>
                    </div>
                    <div className="flex justify-between py-1 text-base">
                        <span className="font-semibold">Remaining balance:</span>
                        <span
                            className={`font-semibold ${
                                this.getRemainingBalance() === 0 ? 'text-green-700' : 'text-yellow-700'
                            }`}
                        >
                            <Price price={this.getRemainingBalance()} />
                        </span>
                    </div>
                </div>
            </div>
        );
    }
}

export default Payments;
